<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AccountId from './components/AccountId.vue';
import BuildInfo from './components/BuildInfo.vue';
import AccountDelete from './components/AccountDelete.vue';
import AutoResolve from './components/AutoResolve.vue';
// NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode section (group-visibility)
import SecureAssignmentMode from './components/SecureAssignmentMode.vue';
import AssignmentConsideration from './components/AssignmentConsideration.vue';
// NOTICE: KAPTION-INTEGRATION — Team Assignment Resolution section (team-assignment-resolution.UI)
import KaptionTeamAssignmentResolution from './components/KaptionTeamAssignmentResolution.vue';
import KaptionTeamFilterScope from './components/KaptionTeamFilterScope.vue';
// NOTICE: KAPTION-INTEGRATION — Smart Assignment section (smart-assignment.FLAG.3)
import KaptionSmartAssignment from './components/KaptionSmartAssignment.vue';
import DefaultAllConversations from './components/DefaultAllConversations.vue';
import WhatsAppAgentSignature from './components/WhatsAppAgentSignature.vue';
import ShowAgentNameInConversations from './components/ShowAgentNameInConversations.vue';
import KaptionDesktopNotificationsPrompt from './components/KaptionDesktopNotificationsPrompt.vue';
// NOTICE: KAPTION-INTEGRATION - Account-wide participant notification opt-in.
import KaptionParticipantNotifications from './components/KaptionParticipantNotifications.vue';
// NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging account default + lock
import KaptionMessageMergeSetting from './components/KaptionMessageMergeSetting.vue';
// NOTICE: KAPTION-INTEGRATION - template-languages.SETTINGS.1
import KaptionTemplateLanguages from './components/KaptionTemplateLanguages.vue';
import SectionLayout from './components/SectionLayout.vue';
// NOTICE: KAPTION-INTEGRATION - partner-brand-themes.SETTINGS.1
import KaptionBrandTheme from './components/KaptionBrandTheme.vue';

export default {
  components: {
    BaseSettingsHeader,
    NextButton,
    AccountId,
    BuildInfo,
    AccountDelete,
    AutoResolve,
    SecureAssignmentMode,
    AssignmentConsideration,
    KaptionTeamAssignmentResolution,
    KaptionTeamFilterScope,
    KaptionSmartAssignment,
    DefaultAllConversations,
    WhatsAppAgentSignature,
    ShowAgentNameInConversations,
    KaptionDesktopNotificationsPrompt,
    KaptionParticipantNotifications,
    KaptionMessageMergeSetting,
    KaptionTemplateLanguages,
    SectionLayout,
    KaptionBrandTheme,
    WithLabel,
    NextInput,
  },
  setup() {
    const { updateUISettings, uiSettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, uiSettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnChatwootCloud: 'globalConfig/isOnChatwootCloud',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },
    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },
    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },
    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },
    currentAccount() {
      return this.getAccount(this.accountId) || {};
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    async initializeAccount() {
      try {
        const { name, locale, id, domain, support_email, features } =
          this.getAccount(this.accountId);

        this.$root.$i18n.locale = this.uiSettings?.locale || locale;
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
      } catch (error) {
        // Ignore error
      }
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
        });
        // If user locale is set, update the locale with user locale
        if (this.uiSettings?.locale) {
          this.$root.$i18n.locale = this.uiSettings?.locale;
        } else {
          // If user locale is not set, update the locale with account locale
          this.$root.$i18n.locale = this.locale;
        }
        this.getAccount(this.id).locale = this.locale;
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col max-w-2xl mx-auto w-full">
    <BaseSettingsHeader :title="$t('GENERAL_SETTINGS.TITLE')" />
    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <SectionLayout
        :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
        :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
      >
        <form
          v-if="!uiFlags.isFetchingItem"
          class="grid gap-4"
          @submit.prevent="updateAccount"
        >
          <WithLabel
            :has-error="v$.name.$error"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
          >
            <NextInput
              v-model="name"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
              @blur="v$.name.$touch"
            />
          </WithLabel>
          <WithLabel
            :has-error="v$.locale.$error"
            :label="$t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL')"
            :error-message="$t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR')"
          >
            <select v-model="locale" class="!mb-0 text-sm">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyDomainEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
          >
            <NextInput
              v-model="domain"
              type="text"
              class="w-full"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
            <template #help>
              {{
                featureInboundEmailEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED')
              }}

              {{
                featureCustomReplyDomainEnabled &&
                $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
              }}
            </template>
          </WithLabel>
          <WithLabel
            v-if="featureCustomReplyEmailEnabled"
            :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
          >
            <NextInput
              v-model="supportEmail"
              type="text"
              class="w-full"
              :placeholder="
                $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
              "
            />
          </WithLabel>
          <div>
            <NextButton blue :is-loading="isUpdating" type="submit">
              {{ $t('GENERAL_SETTINGS.SUBMIT') }}
            </NextButton>
          </div>
        </form>
      </SectionLayout>

      <woot-loading-state v-if="uiFlags.isFetchingItem" />
    </div>
    <!-- NOTICE: KAPTION-INTEGRATION - partner-brand-themes.SETTINGS.1
         (self-hides unless the account has more than one available theme) -->
    <KaptionBrandTheme />
    <AutoResolve v-if="showAutoResolutionConfig" />
    <!-- NOTICE: KAPTION-INTEGRATION — Secure Assignment Mode (self-gated by KAPTION_GROUP_VISIBILITY) -->
    <SecureAssignmentMode />
    <AssignmentConsideration />
    <!-- NOTICE: KAPTION-INTEGRATION — Team Assignment Resolution (self-gated by KAPTION_TEAM_ASSIGNMENT_RESOLUTION) -->
    <KaptionTeamAssignmentResolution />
    <KaptionTeamFilterScope />
    <!-- NOTICE: KAPTION-INTEGRATION — Smart Assignment (self-gated by KAPTION_SMART_ASSIGNMENT) -->
    <KaptionSmartAssignment />
    <!-- NOTICE: KAPTION-INTEGRATION - Default All Conversations toggle -->
    <DefaultAllConversations />
    <!-- NOTICE: KAPTION-INTEGRATION - WhatsApp Agent Signature toggle -->
    <WhatsAppAgentSignature />
    <!-- NOTICE: KAPTION-INTEGRATION - Show Agent Name in Conversations toggle -->
    <ShowAgentNameInConversations />
    <!-- NOTICE: KAPTION-INTEGRATION - desktop-notifications-nudge.ACCOUNT.1
         (self-gated by KAPTION_DESKTOP_NOTIFICATIONS_NUDGE) -->
    <KaptionDesktopNotificationsPrompt />
    <!-- NOTICE: KAPTION-INTEGRATION - Past-participant notifications default off. -->
    <KaptionParticipantNotifications />
    <!-- NOTICE: KAPTION-INTEGRATION - whatsapp-message-merging
         (self-gated by KAPTION_MESSAGE_MERGING) -->
    <KaptionMessageMergeSetting />
    <!-- NOTICE: KAPTION-INTEGRATION - template-languages.SETTINGS.1 -->
    <KaptionTemplateLanguages />
    <AccountId />
    <div v-if="!uiFlags.isFetchingItem && isOnChatwootCloud">
      <AccountDelete />
    </div>
    <BuildInfo />
  </div>
</template>
